var m = require('mithril');
var prop = require('mithril/stream');
var moment = require('moment-timezone');
var downloadReportModal = require('../../layout/components/downloadReportModal');
var loaders = require('../../layout/components/loaders');
var pikaday = require('../../libs/pikaday');
var rangePikaday = require('../../libs/rangePikaday');
var downloadFile = require('./downloadFile');
var ToggleSwitch = require('../../components/toggleSwitch');
var scheduledReportModal = require('../../reports/scheduledReportModal');
var Report = require('../../../../../models/reports/report');
var loadReportRegistry = require('../../reports/loadReportRegistry');
var EventSummaryPanel = require('../../alarms/components/eventSummaryPanel');
var paginationWithSearch = require('../../data/components/paginationWithSearch');
var eventPresentation = require('../../alarms/eventPresentation');
var developerOptions = require('../../reports/developerOptions');

var downloadReportModalInterface = {};
var REPORT_FAKE_TIMESERIES_KEY = 'nimrod:report-fake-timeseries';

function getLocalTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  } catch (e) {
    return 'America/New_York';
  }
}

function getWindowObject() {
  if (typeof window === 'undefined') return null;
  return window;
}

function shouldShowDeveloperReportOptions() {
  return developerOptions.shouldShowDeveloperReportOptions();
}

function getStoredFakeTimeseriesPreference() {
  if (!shouldShowDeveloperReportOptions()) return false;
  var win = getWindowObject();
  if (!win) return false;
  try {
    return win.localStorage && win.localStorage.getItem(REPORT_FAKE_TIMESERIES_KEY) === 'true';
  } catch (e) {
    return false;
  }
}

function setStoredFakeTimeseriesPreference(enabled) {
  var win = getWindowObject();
  if (!win) return;
  try {
    if (win.localStorage) {
      win.localStorage.setItem(REPORT_FAKE_TIMESERIES_KEY, enabled ? 'true' : 'false');
    }
  } catch (e) {}
}

function getErrorMessage(err, fallback) {
  if (!err) return fallback || 'An error occurred';
  if (typeof err === 'string') return err;
  if (err.message) return err.message;
  if (err.error) return err.error;
  return fallback || 'An error occurred';
}

function buildReportLabel(reportType) {
  var definition = Report.getDefinition(reportType) || {};
  return definition.name || reportType || 'Report';
}

function getReportEntryName(reportType) {
  var definition = Report.getDefinition(reportType) || {};
  if (definition.entryName) return definition.entryName;
  if (reportType === 'Custom Export') return 'record';
  var value = String(reportType || '');
  var colonIndex = value.indexOf(':');
  return colonIndex >= 0 ? value.slice(colonIndex + 1) : value;
}

function buildDefaultParams(reportType) {
  var defaults = Report.getScheduleDefaults(reportType);
  return Object.assign({}, defaults && defaults.defaultParams ? defaults.defaultParams : {});
}

function buildScheduleParams(reportType, downloadParams) {
  if (reportType === 'Custom Export') return buildDefaultParams(reportType);
  return Object.assign({}, downloadParams || {});
}

function getAllowedFormats(reportType) {
  var formats = Report.getAllowedDownloadFormats(reportType);
  return Array.isArray(formats) && formats.length ? formats : ['pdf', 'xlsx'];
}

function getFormatLabel(format) {
  if (format === 'xlsx') return 'Excel';
  if (format === 'json') return 'JSON';
  return 'PDF';
}

function getTypeMeta(reportType) {
  return Report.getTypeMeta ? (Report.getTypeMeta(reportType || 'Custom Export') || {}) : {};
}

function getDownloadMetaForOptions(options) {
  if (!options) return {};
  var type = options.reportType || 'Custom Export';
  if (Report.getDownloadMeta) {
    return Report.getDownloadMeta(type) || {};
  }
  var meta = getTypeMeta(type);
  return meta && meta.download ? meta.download : {};
}

function getSavedCompression(reportRecord) {
  var config = reportRecord && reportRecord.exportConfig;
  if (typeof config === 'string') {
    try {
      config = JSON.parse(config);
    } catch (e) {
      return null;
    }
  }
  if (!config || typeof config !== 'object') return null;
  var compression = String(config.compression || '').trim().toLowerCase();
  return ['automatic', 'gzip', 'none'].indexOf(compression) >= 0 ? compression : null;
}

function usesQueuedGeneration(options) {
  var downloadMeta = getDownloadMetaForOptions(options);
  return downloadMeta && downloadMeta.deliveryMode === 'queued';
}

function supportsRollupInterval(options) {
  if (!options) return false;
  if (options.rollupMinutes) return true;
  var defaults = buildDefaultParams(options.reportType);
  return Object.prototype.hasOwnProperty.call(defaults, 'intervalMinutes');
}

function getRollupMinutes(options, fallback) {
  var value = options && options.rollupMinutes;
  if (typeof value === 'function') value = value();
  var numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
}

function normalizeDateSelectionConfig(value) {
  if (value === true) {
    return { enabled: true, mode: 'window' };
  }
  if (typeof value === 'string') {
    return { enabled: true, mode: value === 'range' ? 'range' : 'window' };
  }
  if (value && typeof value === 'object') {
    var enabled = Object.prototype.hasOwnProperty.call(value, 'enabled')
      ? value.enabled !== false
      : true;
    return {
      enabled: enabled,
      mode: value.mode === 'range' ? 'range' : 'window'
    };
  }
  return { enabled: false, mode: null };
}

function getDateSelectionConfigForOptions(options) {
  return normalizeDateSelectionConfig(getDownloadMetaForOptions(options).dateSelection);
}

function hasDateSelection(options) {
  return Boolean(getDateSelectionConfigForOptions(options).enabled);
}

function usesRangeDateSelection(options) {
  var config = getDateSelectionConfigForOptions(options);
  return Boolean(config.enabled && config.mode === 'range');
}

function usesWindowDateSelection(options) {
  var config = getDateSelectionConfigForOptions(options);
  return Boolean(config.enabled && config.mode !== 'range');
}

function supportsEventEntrypoint(reportType) {
  var typeMeta = getTypeMeta(reportType);
  return Array.isArray(typeMeta.entrypoints) && typeMeta.entrypoints.indexOf('event') !== -1;
}

function getEventConfig(reportRecord) {
  if (!reportRecord || !reportRecord.eventConfig) return {};
  if (typeof reportRecord.eventConfig === 'string') {
    try {
      return JSON.parse(reportRecord.eventConfig) || {};
    } catch (e) {
      return {};
    }
  }
  if (typeof reportRecord.eventConfig === 'object') {
    return reportRecord.eventConfig;
  }
  return {};
}

function hasEnabledEventConfig(reportRecord) {
  var eventConfig = getEventConfig(reportRecord);
  return Boolean(eventConfig && eventConfig.enabled);
}

function shouldShowEventList(options) {
  if (!options || options.sourceKind !== 'reportRecord') return false;
  var eventConfig = getEventConfig(options.reportRecord);
  var hasBindings = Array.isArray(eventConfig.bindings) && eventConfig.bindings.length > 0;
  var downloadMeta = getDownloadMetaForOptions(options);
  return Boolean(
    (eventConfig && eventConfig.enabled) ||
    hasBindings ||
    downloadMeta.requiresEvent ||
    downloadMeta.eventSelection ||
    supportsEventEntrypoint(options.reportType)
  );
}

function supportsManualRange(options) {
  if (!options || !options.showEventList || !options.showEventList()) return false;
  if (options.requiresEvent && options.requiresEvent()) return false;
  return hasDateSelection(options);
}

function estimateManualChartSamples(options) {
  var span = Number(options && options.span && options.span());
  if (!Number.isFinite(span) || span <= 0) return null;
  var resample = options && options.resampling ? options.resampling() : null;
  if (resample === 'hours') return Math.ceil(span * 4);
  return Math.ceil(span);
}

function validateCurrentSelection(options) {
  if (!options) return null;
  if (options.requiresEvent && options.requiresEvent && options.requiresEvent() && !(options.event && options.event())) {
    return 'Please select an event.';
  }

  if (!options.inputMode || options.inputMode() !== 'manual' || !usesWindowDateSelection(options)) {
    return null;
  }

  if (supportsRollupInterval(options)) {
    var duration = Number(options.span && options.span());
    if (!Number.isFinite(duration) || duration < 1) {
      return 'Duration must be at least 1.';
    }
    var rollupMinutes = getRollupMinutes(options, NaN);
    if (!Number.isInteger(rollupMinutes) || rollupMinutes < 1 || rollupMinutes > 1440) {
      return 'Rollup must be between 1 minute and 1 day.';
    }
  }

  var downloadMeta = getDownloadMetaForOptions(options);
  var maxChartSamples = downloadMeta && downloadMeta.maxChartSamples ? Number(downloadMeta.maxChartSamples) : null;
  if (!Number.isFinite(maxChartSamples) || maxChartSamples <= 0) {
    return null;
  }

  var estimatedSamples = estimateManualChartSamples(options);
  if (estimatedSamples && estimatedSamples > maxChartSamples) {
    return 'This manual range would create ' + estimatedSamples + ' chart samples. Reduce the range or choose a larger resample (max ' + maxChartSamples + ').';
  }

  return null;
}

function getEventRid(eventRecord) {
  return eventRecord && eventRecord['@rid'] ? String(eventRecord['@rid']).replace(/^#/, '') : null;
}

function sortReportEvents(events) {
  return (Array.isArray(events) ? events.slice() : []).sort(function (a, b) {
    var aStart = a && a.start ? new Date(a.start).getTime() : 0;
    var bStart = b && b.start ? new Date(b.start).getTime() : 0;
    return bStart - aStart;
  });
}

function loadReportEvents(options) {
  if (!options || !options.reportRid || !options.reportEventsLoading) return Promise.resolve([]);
  options.reportEventsLoading(true);
  return loaders.requestWithLoader({
    loader: 'background',
    url: '/api/v1/report/' + String(options.reportRid).replace(/^#/, '') + '/alarmevents',
    method: 'GET',
    background: true
  })
  .then(function (alarmEventRecords) {
    var sortedEvents = sortReportEvents(alarmEventRecords);
    options.reportEvents(sortedEvents);
    options.reportEventsChanged(true);
    if (!options.event() && sortedEvents.length) {
      options.event(getEventRid(sortedEvents[0]));
    }
    m.redraw();
    return sortedEvents;
  })
  .catch(function () {
    options.reportEvents([]);
    options.reportEventsChanged(true);
    m.redraw();
    return [];
  })
  .finally(function () {
    options.reportEventsLoading(false);
    m.redraw();
  });
}

function hasReportRecordDescription(reportRecord) {
  return Boolean(
    reportRecord
    && Object.prototype.hasOwnProperty.call(reportRecord, 'description')
  );
}

function loadReportRecordForOptions(options) {
  if (
    !options
    || !options.reportRid
    || hasReportRecordDescription(options.reportRecord)
    || !loaders.requestWithLoader
  ) {
    return Promise.resolve(options && options.reportRecord ? options.reportRecord : null);
  }

  return loaders.requestWithLoader({
    loader: 'background',
    url: '/api/v1/report/' + String(options.reportRid).replace(/^#/, ''),
    method: 'GET',
    background: true
  })
  .then(function (reportRecord) {
    if (reportRecord && typeof reportRecord === 'object') {
      options.reportRecord = Object.assign({}, reportRecord, options.reportRecord || {});
      if (!options.reportType && options.reportRecord.type) {
        options.reportType = options.reportRecord.type;
      }
    }
    return options.reportRecord || null;
  })
  .catch(function () {
    return null;
  });
}

function setInputMode(options, mode) {
  if (!options || !options.inputMode) return;
  options.inputMode(mode);
  if (options.validationError) {
    options.validationError(null);
  }
  if (mode === 'manual' && options.event) {
    options.event(null);
  }
}

function getSelectedEventRecord(options) {
  var selectedEventRid = options && options.event ? options.event() : null;
  if (!selectedEventRid || !options || !options.reportEvents) return null;
  return (options.reportEvents() || []).find(function (eventRecord) {
    return getEventRid(eventRecord) === selectedEventRid;
  }) || null;
}

function eventCellClass() {
  return 'pv2 ph3 bb bg-dark-gray b--near-black';
}

function eventIdCellClass() {
  return 'pv2 ph3 bb bg-dark-gray b--near-black';
}

function eventSelectionCellClass(isSelected) {
  return 'pv2 ph3 bb tc bg-dark-gray b--near-black' + (isSelected ? '' : ' moon-gray');
}

function eventModeToggleView(options) {
  if (!options || !options.showEventList || !options.showEventList()) return null;

  if (options.inputMode() === 'event' && supportsManualRange(options)) {
    return m('div.mt3', [
      m('a.blue.dim.pointer', {
        onclick: function (ev) {
          ev.preventDefault();
          setInputMode(options, 'manual');
        }
      }, 'Select manual range...')
    ]);
  }

  if (options.inputMode() === 'manual') {
    return m('div.mt3', [
      m('a.blue.dim.pointer', {
        onclick: function (ev) {
          ev.preventDefault();
          setInputMode(options, 'event');
        }
      }, 'Use event selection instead...')
    ]);
  }

  return null;
}

function recordRangeControlsView(options) {
  var supportsRollup = supportsRollupInterval(options);
  return m('div.flex.justify-between.flex-wrap', [
    m('form.flex.flex-wrap', { role: 'form' }, [
      m('div.mr2', [
        m('label', { for: 'report-record-span-input' }, supportsRollup ? 'Duration' : 'Span'),
        m('input.mb0.w4', {
          id: 'report-record-span-input',
          style: { width: '5em' },
          type: 'number',
          autocomplete: 'off',
          value: options.span(),
          oninput: function (ev) {
            options.span(ev.target.value);
            options.validationError(null);
          }
        })
      ]),
      m('div.mr2', [
        m('label', { for: 'report-record-sampling-input' }, supportsRollup ? 'Duration unit' : 'Sampling'),
        m('select.input-reset.mb0.w4', {
          id: 'report-record-sampling-input',
          autocomplete: 'off',
          onchange: function (ev) {
            options.resampling(ev.target.value);
            options.validationError(null);
          },
          value: options.resampling()
        }, supportsRollup ? [
          m('option', { value: 'hours' }, 'Hours'),
          m('option', { value: 'days' }, 'Days'),
          m('option', { value: 'weeks' }, 'Weeks')
        ] : [
          m('option', { value: 'days' }, 'Days'),
          m('option', { value: 'hour' }, 'Hours'),
          m('option', { value: 'hours' }, 'Hours (15m)'),
          m('option', { value: 'minutes' }, 'Minutes')
        ])
      ]),
      supportsRollup ? m('div.mr2', [
        m('label', { for: 'report-record-rollup-input' }, 'Rollup'),
        m('select.input-reset.mb0.w4', {
          id: 'report-record-rollup-input',
          autocomplete: 'off',
          onchange: function (ev) {
            options.rollupMinutes(Number(ev.target.value));
            options.validationError(null);
          },
          value: String(options.rollupMinutes())
        }, [
          m('option', { value: '5' }, '5 minutes'),
          m('option', { value: '15' }, '15 minutes'),
          m('option', { value: '30' }, '30 minutes'),
          m('option', { value: '60' }, '1 hour'),
          m('option', { value: '360' }, '6 hours'),
          m('option', { value: '1440' }, '1 day')
        ])
      ]) : null,
      m('div', { style: { width: '11.5em' } }, [
        m('label', { for: 'report-record-end-date' }, 'Ending'),
        m(pikaday, { value: options.endDate, dateFormat: 'm/d/Y h:i K' })
      ])
    ])
  ]);
}

function renderValidation(options) {
  return options && options.validationError && options.validationError()
    ? m('div.red.mt3', options.validationError())
    : null;
}

function eventTableView(options) {
  var events = sortReportEvents(options.reportEvents());
  var pagedEvents = options.reportEventsToShow;
  var visibleEvents = pagedEvents.some(function (eventRecord) {
    return eventRecord && eventRecord['@rid'];
  }) ? pagedEvents : events.slice(0, 5);
  var selectedEventRecord = getSelectedEventRecord(options);

  return m('div.mv4', [
    options.reportEventsLoading()
      ? m('div.moon-gray', 'Loading events...')
      : m(paginationWithSearch, {
          data: events,
          newNode: options.reportEventsChanged,
          dataToShow: pagedEvents,
          itemsPerPage: 5,
          hideSearch: true,
          search: ''
        }, [
          m('div.overflow-auto', [
            m('table.w-100.f6.collapse', [
              m('thead', [
                m('tr', [
                  m('th.tl.pb2.ph3.bb.b--dark-gray.fw4.moon-gray', 'Start'),
                  m('th.tl.pb2.ph3.bb.b--dark-gray.fw4.moon-gray', 'End'),
                  m('th.tl.pb2.ph3.bb.b--dark-gray.fw4.moon-gray', 'Status'),
                  m('th.tl.pb2.ph3.bb.b--dark-gray.fw4.moon-gray', 'Event ID'),
                  m('th.tc.pb2.ph3.bb.b--dark-gray.fw4.moon-gray', 'Select')
                ])
              ]),
              m('tbody', [
                events.length === 0
                  ? m('tr', [
                      m('td.pv3.ph3.moon-gray', { colspan: 5 }, 'No events found for this report.')
                    ])
                  : visibleEvents.map(function (eventRecord, idx) {
                      if (!eventRecord || !eventRecord['@rid']) {
                        return m('tr', { key: 'event-placeholder-' + idx }, [
                          m('td.pv2.ph3.bb.bg-dark-gray.b--near-black', { colspan: 5 }, '\u00a0')
                        ]);
                      }
                      var eventRid = getEventRid(eventRecord);
                      var isSelected = options.event() === eventRid;
                      return m('tr.pointer' + (isSelected ? '.shadow-1' : ''), {
                        key: eventRecord['@rid'],
                        onclick: function () {
                          options.event(eventRid);
                          options.validationError(null);
                        }
                      }, [
                        m('td.' + eventCellClass(isSelected), eventPresentation.formatEventDate(eventRecord.start, getLocalTimezone())),
                        m('td.' + eventCellClass(isSelected), eventRecord.end ? eventPresentation.formatEventDate(eventRecord.end, getLocalTimezone()) : 'Active'),
                        m('td.' + eventCellClass(isSelected), eventRecord.end ? 'Closed' : 'Active'),
                        m('td.' + eventIdCellClass(isSelected), eventRecord.eventId || eventRid || ''),
                        m('td.' + eventSelectionCellClass(isSelected), [
                          m('i', {
                            class: isSelected ? 'ion-ios-circle-filled f4 blue' : 'ion-ios-circle-outline f4'
                          })
                        ])
                      ]);
                    })
              ])
            ])
          ])
        ]),
    selectedEventRecord ? m(EventSummaryPanel, {
      title: 'Selected Event',
      eventRecord: selectedEventRecord,
      contextLocation: options.contextLocation,
      timezone: getLocalTimezone()
    }) : null,
    eventModeToggleView(options),
    renderValidation(options)
  ]);
}

function resolveReportUi(options) {
  var definition = Report.getDefinition(options && options.reportType) || {};
  var matcher = {};

  if (options && (options.sourceKind === 'reportRecord' || options.reportRid)) {
    matcher.targetKind = 'reportRecord';
  } else if (options && options.targetKind) {
    matcher.targetKind = options.targetKind;
  }

  var variant = matcher.targetKind
    ? Report.getDefinitionVariant(options && options.reportType, matcher)
    : null;

  return Object.assign({}, definition && definition.ui ? definition.ui : {}, variant && variant.ui ? variant.ui : {});
}

function getReportHelpParagraphs(options) {
  var reportDescription = options
    && options.reportRecord
    && typeof options.reportRecord.description === 'string'
    ? options.reportRecord.description.trim()
    : '';
  if (reportDescription) {
    return [reportDescription];
  }

  var ui = resolveReportUi(options);
  var helpText = ui && ui.helpText;
  if (Array.isArray(helpText)) {
    return helpText.map(function (value) {
      return String(value || '').trim();
    }).filter(Boolean);
  }
  if (typeof helpText === 'string' && helpText.trim()) {
    return [helpText.trim()];
  }
  return [];
}

function renderReportHelp(options) {
  var paragraphs = getReportHelpParagraphs(options);
  if (!paragraphs.length) return null;

  return m('div.mb3.pa3.bg-near-black.ba.b--white-10.br2', paragraphs.map(function (paragraph, index) {
    return m('p.light-silver.lh-copy' + (index === 0 ? '.mt0' : '') + (index === paragraphs.length - 1 ? '.mb0' : '.mb2'), paragraph);
  }));
}

function renderSelectedEventSummary(options) {
  if (!options || !options.eventRecord) return null;
  return m(EventSummaryPanel, {
    title: 'Selected Event',
    eventRecord: options.eventRecord,
    contextLocation: options.contextLocation,
    timezone: getLocalTimezone()
  });
}

function resolveDirectReportType(options) {
  if (!options) return null;
  if (options.reportType && options.reportType !== 'Custom Export' && Object.keys(Report.getDefinition(options.reportType) || {}).length) {
    return options.reportType;
  }

  var reportMode = options.reportMode || null;
  var targetKind = options.targetKind || null;
  var targetClassName = options.targetClassName || null;
  if (!reportMode || !targetKind) return options.reportType || null;

  var definitions = Report.reportDefinitions || {};
  var matches = Object.keys(definitions).map(function (key) {
    return definitions[key];
  }).filter(function (definition, index, list) {
    if (!definition || !definition.id) return false;
    if (list.indexOf(definition) !== index) return false;
    if (definition.entryName !== reportMode) return false;
    return Array.isArray(definition.variants) && definition.variants.some(function (variant) {
      var variantTargetKind = variant && variant.targetConfig ? variant.targetConfig.targetKind : null;
      var allowedClasses = variant && variant.targetConfig && Array.isArray(variant.targetConfig.allowedClasses)
        ? variant.targetConfig.allowedClasses
        : [];
      if (variantTargetKind !== targetKind) return false;
      if (!targetClassName || !allowedClasses.length) return true;
      return allowedClasses.indexOf(targetClassName) >= 0;
    });
  });

  if (!matches.length) return options.reportType || null;
  return matches[0].id;
}

function resolveReportRecordType(options) {
  if (!options) return null;
  var reportType = options.reportType || null;
  if (!reportType) return null;
  if (Object.keys(Report.getDefinition(reportType) || {}).length) {
    return reportType;
  }

  var definitions = Report.reportDefinitions || {};
  var matches = Object.keys(definitions).map(function (key) {
    return definitions[key];
  }).filter(function (definition, index, list) {
    if (!definition || !definition.id) return false;
    if (list.indexOf(definition) !== index) return false;
    if (definition.templateName !== reportType) return false;
    return Array.isArray(definition.variants) && definition.variants.some(function (variant) {
      return variant && variant.dataScope === 'reportRecord';
    });
  });

  if (!matches.length) return reportType;
  return matches[0].id;
}

function toDownloadPayload(options, format) {
  var entryName = getReportEntryName(options.reportType);
  var isReportRecord = options && (options.sourceKind === 'reportRecord' || options.reportRid);
  var payload = {
    reportType: options.reportType,
    reportName: options.reportRecord && options.reportRecord.name
      ? options.reportRecord.name
      : (options.reportName || buildReportLabel(options.reportType)),
    reportMode: options.reportMode || entryName || null,
    sourceKind: isReportRecord ? 'reportRecord' : 'direct',
    reportRid: isReportRecord ? options.reportRid : null,
    targetKind: isReportRecord ? null : options.targetKind,
    targetRid: isReportRecord ? null : options.targetRid,
    targetClassName: isReportRecord ? null : (options.targetClassName || null),
    format: format,
    params: Object.assign({}, buildDefaultParams(options.reportType), {
      timezone: getLocalTimezone()
    })
  };

  var selectedEventRid = options.eventRid
    || (options.inputMode && options.inputMode() === 'event' && options.event ? options.event() : null);
  if (selectedEventRid) {
    payload.eventRid = selectedEventRid;
  }

  if (options.fakeTimeseries && options.fakeTimeseries() && shouldShowDeveloperReportOptions()) {
    payload.params.fakeTimeseries = true;
  }

  var savedCompression = getSavedCompression(options.reportRecord);
  if (savedCompression) {
    payload.params.compression = savedCompression;
  }

  if (usesRangeDateSelection(options) && options.dates && Array.isArray(options.dates())) {
    payload.params.start = moment(options.dates()[0]).valueOf();
    payload.params.stop = moment(options.dates()[1]).valueOf();
    payload.params.resample = 'days';
    payload.params.bucket = '10_years';
    payload.params.useNow = false;
  } else if (usesWindowDateSelection(options)) {
    payload.params.start = moment(options.endDate()).valueOf();
    payload.params.span = Number(options.span());
    payload.params.resample = options.resampling();
    if (supportsRollupInterval(options)) {
      payload.params.intervalMinutes = getRollupMinutes(options, payload.params.intervalMinutes || 15);
    }
    payload.params.useNow = false;
  }

  return payload;
}

function showDownloadError(err, fallback) {
  downloadReportModal.vm.title('Error');
  downloadReportModal.vm.errorMessage = prop(getErrorMessage(err, fallback));
  if (downloadReportModalInterface.options && downloadReportModalInterface.options.loading) {
    downloadReportModalInterface.options.loading(false);
  }
  m.redraw();
}

function developerOptionsView() {
  if (!shouldShowDeveloperReportOptions()) return null;

  return m('div.mt3.pa3.ba.b--black-10.br2', [
    m('div.flex.items-center.justify-between', [
      m('span', 'Use fake timeseries data'),
      m(ToggleSwitch, {
        id: 'unified-report-download-fake-timeseries-toggle',
        checked: Boolean(downloadReportModalInterface.options && downloadReportModalInterface.options.fakeTimeseries && downloadReportModalInterface.options.fakeTimeseries()),
        ariaLabel: 'Use fake timeseries data for this report download',
        onchange: function (ev) {
          var enabled = Boolean(ev.target.checked);
          downloadReportModalInterface.options.fakeTimeseries(enabled);
          setStoredFakeTimeseriesPreference(enabled);
        }
      })
    ]),
    m('div.gray.f6.mt2', 'Developer option for chart/layout verification when historical data is unavailable.')
  ]);
}

function submitUnifiedDownload(format) {
  var options = downloadReportModalInterface.options;
  if (!options.reportType) {
    showDownloadError(new Error('Unable to resolve the selected report type.'));
    return;
  }
  var validationError = validateCurrentSelection(options);
  if (validationError) {
    options.validationError(validationError);
    m.redraw();
    return;
  }
  var queued = usesQueuedGeneration(options);
  downloadReportModal.vm.buttons([]);
  downloadReportModal.vm.title(queued ? 'Queueing Download' : 'Downloading');
  options.loading(true);

  loaders.requestWithLoader({
    loader: 'main',
    url: queued ? '/api/v1/report/execute/queued' : '/api/v1/report/execute',
    method: 'POST',
    body: toDownloadPayload(options, format),
    background: true
  })
  .then(function (res) {
    if (queued && res && res.uid && res.reportName) {
      downloadReportModalInterface.close({
        uidRequest: res.uid,
        reportName: res.reportName,
        execution: res
      });
      return;
    }
    if (res && res.status === 'ready' && res.filename) {
      var downloadUrl = res.downloadUrl || ('/report/download/' + res.filename);
      return downloadFile(downloadUrl, { filename: res.filename })
        .then(function () {
          downloadReportModalInterface.close();
        });
    }
    throw new Error(res && (res.error || res.message) ? (res.error || res.message) : 'Report export failed');
  })
  .catch(function (err) {
    showDownloadError(err);
  });
}

function openScheduleDialog(options) {
  if (!options.reportType) {
    showDownloadError(new Error('Unable to resolve the selected report type.'));
    return;
  }
  var validationError = validateCurrentSelection(options);
  if (validationError) {
    options.validationError(validationError);
    m.redraw();
    return;
  }
  var sourceKind = options.sourceKind || (options.reportRid ? 'reportRecord' : 'direct');
  var triggerType = options.showEventList && options.inputMode && options.inputMode() === 'event'
    ? 'event'
    : 'cron';
  var payload = toDownloadPayload(options, getAllowedFormats(options.reportType)[0]);
  var scheduleOptions = {
    modalTitle: options.modalTitle,
    reportType: options.reportType,
    reportMode: options.reportMode || getReportEntryName(options.reportType),
    reportRid: sourceKind === 'reportRecord' ? (options.reportRid || null) : null,
    sourceKind: sourceKind,
    targetKind: sourceKind === 'direct' ? options.targetKind : null,
    targetRid: sourceKind === 'direct' ? options.targetRid : null,
    targetClassName: sourceKind === 'direct' ? (options.targetClassName || null) : null,
    timezone: getLocalTimezone(),
    format: getAllowedFormats(options.reportType)[0],
    params: buildScheduleParams(options.reportType, payload.params),
    triggerType: triggerType,
    downloadRequiresEvent: triggerType === 'event',
    supportsEventNotifications: hasEnabledEventConfig(options.reportRecord)
  };
  downloadReportModalInterface.close();
  scheduledReportModal.editDialogue(scheduleOptions, { lockTriggerType: Boolean(options.requiresEvent && options.requiresEvent()) });
}

function renderButtons() {
  var options = downloadReportModalInterface.options;

  if (options.assetsIds) {
    return [{
      label: 'Download',
      func: function () {
        downloadReportModal.vm.buttons([]);
        downloadReportModal.vm.title('Downloading');
        options.loading(true);
        loaders.requestWithLoader({
          loader: 'main',
          url: '/api/v1/assets/certexport?' + m.buildQueryString({ assetsIds: options.assetsIds, timezone: getLocalTimezone() }),
          method: 'POST',
          background: true
        })
        .then(function (res) {
          if (res && res.uid && res.reportName) {
            downloadReportModalInterface.close({ uidRequest: res.uid, reportName: res.reportName });
            return;
          }
          throw new Error('Calibration export failed');
        })
        .catch(function (err) {
          showDownloadError(err);
        });
      }
    }];
  }

  if (options.reportMode === 'calibration') {
    return [{
      label: 'Download',
      func: function () {
        downloadReportModal.vm.buttons([]);
        downloadReportModal.vm.title('Downloading');
        options.loading(true);
        loaders.requestWithLoader({
          loader: 'main',
          url: '/api/v1/assets/certexport/' + options.targetRid + '/' + options.targetClassName + '?timezone=' + encodeURIComponent(getLocalTimezone()),
          method: 'GET',
          background: true
        })
        .then(function (res) {
          if (res && res.uid && res.reportName) {
            downloadReportModalInterface.close({ uidRequest: res.uid, reportName: res.reportName });
            return;
          }
          throw new Error('Calibration export failed');
        })
        .catch(function (err) {
          showDownloadError(err);
        });
      }
    }];
  }

  var buttons = [{
    label: 'Schedule',
    class: 'fl',
    func: function () { openScheduleDialog(options); }
  }];

  getAllowedFormats(options.reportType).forEach(function (format) {
    buttons.push({
      label: getFormatLabel(format),
      class: format === 'xlsx' ? 'fr green' : (format === 'json' ? 'fr ml2 blue' : 'fr ml2 red'),
      func: function () { submitUnifiedDownload(format); }
    });
  });

  return buttons;
}

downloadReportModalInterface.editDialogue = function (options) {
  return loadReportRegistry().catch(function () { return null; }).then(function () {
    downloadReportModalInterface.options = Object.assign({}, options || {});
    downloadReportModalInterface.options.loading = prop(false);
    downloadReportModalInterface.options.fakeTimeseries = prop(getStoredFakeTimeseriesPreference());
    downloadReportModalInterface.options.validationError = prop(null);
    downloadReportModalInterface.options.event = prop(downloadReportModalInterface.options.eventRid || null);
    downloadReportModalInterface.options.reportEvents = prop([]);
    downloadReportModalInterface.options.reportEventsLoading = prop(false);
    downloadReportModalInterface.options.reportEventsChanged = prop(false);
    downloadReportModalInterface.options.reportEventsToShow = [];

    if (!downloadReportModalInterface.options.reportType && options && options.reportName === 'AutomaticTransferSwitch-event') {
      downloadReportModalInterface.options.reportType = 'AutomaticTransferSwitch:event';
      downloadReportModalInterface.options.targetKind = 'location';
      downloadReportModalInterface.options.targetRid = options.locationRid;
      downloadReportModalInterface.options.eventRid = options.eventRecordRid;
      downloadReportModalInterface.options.targetClassName = 'AutomaticTransferSwitch';
    }

    return loadReportRecordForOptions(downloadReportModalInterface.options).then(function () {
      var sourceKind = downloadReportModalInterface.options.sourceKind || (downloadReportModalInterface.options.reportRid ? 'reportRecord' : 'direct');
      downloadReportModalInterface.options.sourceKind = sourceKind;
      if (sourceKind === 'direct') {
        downloadReportModalInterface.options.reportType = resolveDirectReportType(downloadReportModalInterface.options);
      } else {
        downloadReportModalInterface.options.reportType = resolveReportRecordType(downloadReportModalInterface.options);
      }

      var downloadMeta = getDownloadMetaForOptions(downloadReportModalInterface.options);
      var requiresEvent = Boolean(downloadMeta && downloadMeta.requiresEvent) && !supportsEventEntrypoint(downloadReportModalInterface.options.reportType);
      downloadReportModalInterface.options.requiresEvent = prop(requiresEvent);
      downloadReportModalInterface.options.showEventList = prop(shouldShowEventList(downloadReportModalInterface.options));
      downloadReportModalInterface.options.inputMode = prop(
        downloadReportModalInterface.options.showEventList() ? 'event' : 'manual'
      );

      if (usesRangeDateSelection(downloadReportModalInterface.options) && !downloadReportModalInterface.options.dates) {
        var defaultRangeParams = buildDefaultParams(downloadReportModalInterface.options.reportType);
        var rangeStop = moment();
        var rangeSpan = Number(defaultRangeParams.span) || 30;
        var rangeUnit = defaultRangeParams.resample || 'days';
        downloadReportModalInterface.options.dates = prop([moment(rangeStop).subtract(rangeSpan, rangeUnit), rangeStop]);
      } else if (Array.isArray(downloadReportModalInterface.options.dates)) {
        downloadReportModalInterface.options.dates = prop(downloadReportModalInterface.options.dates);
      }

      if (!downloadReportModalInterface.options.endDate) {
        downloadReportModalInterface.options.endDate = prop(moment());
      }
      if (!downloadReportModalInterface.options.span) {
        var defaultParams = buildDefaultParams(downloadReportModalInterface.options.reportType);
        downloadReportModalInterface.options.span = prop(defaultParams.span || 24);
      } else if (typeof downloadReportModalInterface.options.span !== 'function') {
        downloadReportModalInterface.options.span = prop(downloadReportModalInterface.options.span);
      }
      if (!downloadReportModalInterface.options.resampling) {
        var params = buildDefaultParams(downloadReportModalInterface.options.reportType);
        downloadReportModalInterface.options.resampling = prop(params.resample || 'hours');
      } else if (typeof downloadReportModalInterface.options.resampling !== 'function') {
        downloadReportModalInterface.options.resampling = prop(downloadReportModalInterface.options.resampling);
      }
      if (supportsRollupInterval(downloadReportModalInterface.options)) {
        var rollupDefaults = buildDefaultParams(downloadReportModalInterface.options.reportType);
        if (!downloadReportModalInterface.options.rollupMinutes) {
          downloadReportModalInterface.options.rollupMinutes = prop(Number(rollupDefaults.intervalMinutes) || 15);
        } else if (typeof downloadReportModalInterface.options.rollupMinutes !== 'function') {
          downloadReportModalInterface.options.rollupMinutes = prop(Number(downloadReportModalInterface.options.rollupMinutes));
        }
      }

      if (
        downloadReportModalInterface.options.showEventList() &&
        downloadReportModalInterface.options.reportRid
      ) {
        loadReportEvents(downloadReportModalInterface.options);
      }

      downloadReportModal.vm.title(downloadReportModalInterface.options.modalTitle || ('Download ' + buildReportLabel(downloadReportModalInterface.options.reportType)));
      downloadReportModal.vm.body = downloadReportModalInterface.view;
      downloadReportModal.vm.buttons(renderButtons());
      downloadReportModal.vm.open({});
      m.redraw();
    });
  });
};

downloadReportModalInterface.close = function (params) {
  downloadReportModal.vm.close(params);
  m.redraw();
};

downloadReportModalInterface.view = function () {
  var options = downloadReportModalInterface.options || {};
  if (options.loading && options.loading()) {
    return m('div', { role: 'form' }, [
      m('label', 'We are generating the report, please wait.'),
      m('img.h1', { src: '/images/loading-bars.svg' })
    ]);
  }

  if (downloadReportModal.vm.title() === 'Error') {
    return m('div', { role: 'form' }, [
      m('label', 'There was an error generating the report. ' + downloadReportModal.vm.errorMessage())
    ]);
  }

  if (options.showEventList && options.showEventList() && options.inputMode && options.inputMode() === 'event') {
    return m('div', [
      renderReportHelp(options),
      eventTableView(options),
      developerOptionsView()
    ]);
  }

  if (usesRangeDateSelection(options)) {
    return m('div', [
      renderReportHelp(options),
      renderSelectedEventSummary(options),
      m('form', { role: 'form' }, [
        m('div.flex.items-center.flex-wrap', [
          m('div', [
            m('label.mr2.nowrap', 'From:')
          ]),
          m('div', [
            m(rangePikaday, {
              id: 'long-date-report',
              value: options.dates,
              dateFormat: 'm/d/Y h:i K'
            })
          ])
        ])
      ]),
      eventModeToggleView(options),
      renderValidation(options),
      developerOptionsView()
    ]);
  }

  if (usesWindowDateSelection(options)) {
    return m('div', [
      renderReportHelp(options),
      renderSelectedEventSummary(options),
      recordRangeControlsView(options),
      eventModeToggleView(options),
      renderValidation(options),
      developerOptionsView()
    ]);
  }

  return m('div', [
    renderReportHelp(options),
    renderSelectedEventSummary(options),
    renderValidation(options),
    developerOptionsView()
  ]);
};

downloadReportModalInterface._private = {
  shouldShowDeveloperReportOptions: shouldShowDeveloperReportOptions,
  getStoredFakeTimeseriesPreference: getStoredFakeTimeseriesPreference,
  getDateSelectionConfigForOptions: getDateSelectionConfigForOptions,
  buildScheduleParams: buildScheduleParams,
  getReportHelpParagraphs: getReportHelpParagraphs,
  loadReportRecordForOptions: loadReportRecordForOptions,
  usesQueuedGeneration: usesQueuedGeneration,
  supportsRollupInterval: supportsRollupInterval,
  getSavedCompression: getSavedCompression,
  toDownloadPayload: toDownloadPayload
};

module.exports = downloadReportModalInterface;
